import type { StudyArm, AucResult } from '@bioavail/shared-types';
import { normalizeTime, normalizeConcentration } from '../units/normalize';

/**
 * Compute MRT and MAT (PSD §4.2.7 / Phase 2, F-018).
 * AUMC via trapezoidal integration of (t·C) vs t. MRT = AUMC/AUC per arm.
 * MAT = MRT_oral − MRT_iv only when an IV arm is present. MAT < 0 → warn.
 */
export function computeMrtMat(
  arms: StudyArm[],
  aucResults: AucResult[],
): { mrt: { armId: string; value: number }[]; mat: number | null; matNegative: boolean } {
  const mrtList: { armId: string; value: number }[] = [];
  let mrtIv: number | null = null;
  let mrtOral: number | null = null;

  for (const arm of arms) {
    const auc = aucResults.find((a) => a.armId === arm.id);
    if (!auc || arm.usesDirectAuc) continue;

    // Normalize points to canonical hours / ng/mL before integration so the
    // resulting AUMC shares the same unit basis (ng/mL·h²) as the normalized
    // AUC it's divided by (finding E1).
    const points = [...arm.dataPoints]
      .sort((a, b) => a.time - b.time)
      .map((p) => ({
        time: normalizeTime(p.time, arm.timeUnit),
        concentration: normalizeConcentration(p.concentration, arm.concentrationUnit),
      }));
    if (points.length < 2) continue;

    // AUMC = ∫ t·C dt via linear trapezoidal on (t, t·C).
    let aumc = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i]!;
      const p2 = points[i + 1]!;
      const dt = p2.time - p1.time;
      if (dt === 0) continue;
      const tc1 = p1.time * p1.concentration;
      const tc2 = p2.time * p2.concentration;
      aumc += ((tc1 + tc2) / 2) * dt;
    }

    const aucValue = auc.aucZeroToInf > 0 ? auc.aucZeroToInf : auc.aucZeroToT;
    const mrt = aucValue > 0 ? aumc / aucValue : 0;
    mrtList.push({ armId: arm.id, value: mrt });

    if (arm.route === 'IV') {
      mrtIv = mrt;
    } else if (mrtOral === null) {
      mrtOral = mrt;
    }
  }

  let mat: number | null = null;
  let matNegative = false;
  if (mrtIv !== null && mrtOral !== null) {
    mat = mrtOral - mrtIv;
    matNegative = mat < 0;
  }

  return { mrt: mrtList, mat, matNegative };
}
